"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.createProgressiveQueryHook = exports.createQueryHook = exports.createDocumentHook = exports.cleanObject = void 0;
const react_1 = require("react");
const react_smart_hook_firebase_1 = require("@smart-hook/react-smart-hook-firebase");
const react_hook_retention_cache_1 = require("@smart-hook/react-hook-retention-cache");
const fast_json_stable_stringify_1 = __importDefault(require("fast-json-stable-stringify"));
const mingo_1 = require("mingo");
const core_1 = require("mingo/core");
const util_1 = require("mingo/util");
const firestore_web_1 = require("comodel/firestore-web");
const firestore_1 = require("comodel/firestore");
Object.defineProperty(exports, "cleanObject", { enumerable: true, get: function () { return firestore_1.cleanObject; } });
const firestore_2 = require("firebase/firestore");
const hooks_1 = require("common/hooks");
(0, core_1.useOperators)(core_1.OperatorType.QUERY, {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    $matchIntl: (selector, search) => (obj) => {
        const value = (0, util_1.resolve)(obj, selector);
        if (typeof search !== "string")
            return false;
        if (typeof value === "object") {
            return Object.values(value).some((v) => typeof v === "string" ? v.includes(search) : false);
        }
        else if (typeof value === "string") {
            return value.includes(search);
        }
        return false;
    },
    $truthy: (selector, search) => (obj) => {
        const value = (0, util_1.resolve)(obj, selector);
        const match = typeof search === "string" ? JSON.parse(search) : !!search;
        return !!value === match;
    },
});
const useDocumentData = (0, react_smart_hook_firebase_1.createDocumentRefHook)();
const useQueryData = (({ withData, retentionTime } = {}) => (0, react_hook_retention_cache_1.createStoreCacheHook)((0, react_smart_hook_firebase_1.getQueryRefCache)({ withData, retentionTime }), {}))();
const stringifyArray = (array) => array.map(fast_json_stable_stringify_1.default);
const createDocumentHook = (collectionDef, log) => {
    return (constraint) => {
        const accessor = (0, react_1.useMemo)(() => {
            return (0, firestore_web_1.documentAccessor)(collectionDef, constraint, log);
        }, [(0, fast_json_stable_stringify_1.default)(constraint)]);
        const { snapshot, loading, error } = useDocumentData(accessor.ref);
        const data = (0, react_1.useMemo)(() => (snapshot === null || snapshot === void 0 ? void 0 : snapshot.data()) || accessor.defaultValue, [snapshot]);
        (0, react_1.useMemo)(() => {
            if (error)
                console.error(error);
        }, [error]);
        return Object.assign(Object.assign({ data,
            loading,
            error }, accessor), { 
            // confirm to exist by checking real data
            isUpdate: !!(snapshot === null || snapshot === void 0 ? void 0 : snapshot.exists()), 
            // confirm not to exist by checking real data
            isNew: !accessor.ref || (snapshot && !(snapshot === null || snapshot === void 0 ? void 0 : snapshot.exists())), 
            // type checking only
            isEditing: !!accessor.ref });
    };
};
exports.createDocumentHook = createDocumentHook;
const createQueryHook = (collectionDef, queryGenerator) => {
    return (...args) => {
        const { queryParams, constraint, filterParams } = (0, react_1.useMemo)(() => {
            const params = queryGenerator(...args) ||
                {};
            (0, firestore_1.cleanObject)(params);
            return params;
        }, stringifyArray(args));
        const filter = (0, react_1.useMemo)(() => {
            const mingoQuery = new mingo_1.Query(filterParams || {});
            return mingoQuery.test.bind(mingoQuery);
        }, [(0, fast_json_stable_stringify_1.default)(filterParams)]);
        const accessor = (0, react_1.useMemo)(() => {
            return (queryParams &&
                (0, firestore_web_1.queryAccessor)(collectionDef, constraint || {}, queryParams));
        }, stringifyArray([constraint, queryParams]));
        const { snapshot, loading, error } = useQueryData(accessor === null || accessor === void 0 ? void 0 : accessor.ref);
        const list = (0, react_1.useMemo)(() => snapshot === null || snapshot === void 0 ? void 0 : snapshot.docs.map((doc) => doc.data()).filter(filter), [snapshot, filter]);
        (0, react_1.useMemo)(() => {
            if (error)
                console.error(error);
        }, [error]);
        return Object.assign(Object.assign({ list,
            loading,
            error }, accessor), { get: () => __awaiter(void 0, void 0, void 0, function* () {
                const list = yield (accessor === null || accessor === void 0 ? void 0 : accessor.get());
                return list === null || list === void 0 ? void 0 : list.filter(filter);
            }) });
    };
};
exports.createQueryHook = createQueryHook;
const createProgressiveQueryHook = (collectionDef, queryGenerator) => {
    return (...args) => {
        const maxItems = 500;
        const firstMaxItem = 100;
        const prefetchItems = 50;
        const [range, setRange] = (0, react_1.useState)({
            max: 0,
        });
        const [list, setList] = (0, react_1.useState)([]);
        const [lastSnapshot, setLastSnapshot] = (0, react_1.useState)();
        const [error, setError] = (0, react_1.useState)();
        const [fetching, setFetching] = (0, react_1.useState)();
        const { queryParams, constraint, filterParams } = (0, react_1.useMemo)(() => {
            const params = queryGenerator(...args) ||
                {};
            (0, firestore_1.cleanObject)(params);
            return params;
        }, stringifyArray(args));
        const filter = (0, react_1.useMemo)(() => {
            const mingoQuery = new mingo_1.Query(filterParams || {});
            return mingoQuery.test.bind(mingoQuery);
        }, [(0, fast_json_stable_stringify_1.default)(filterParams)]);
        const accessor = (0, react_1.useMemo)(() => {
            return (queryParams &&
                (0, firestore_web_1.queryAccessor)(collectionDef, constraint || {}, queryParams));
        }, stringifyArray([constraint, queryParams]));
        const accessorRef = (0, react_1.useRef)(accessor);
        accessorRef.current = accessor;
        const refresh = () => __awaiter(void 0, void 0, void 0, function* () {
            if (!(accessor === null || accessor === void 0 ? void 0 : accessor.ref)) {
                return;
            }
            if (fetching) {
                return;
            }
            try {
                if (lastSnapshot === false) {
                    return;
                }
                const currentMaxItem = lastSnapshot ? maxItems : firstMaxItem;
                const ref = lastSnapshot
                    ? (0, firestore_2.query)(accessor.ref, (0, firestore_2.startAfter)(lastSnapshot), (0, firestore_2.limit)(currentMaxItem))
                    : (0, firestore_2.query)(accessor.ref, (0, firestore_2.limit)(currentMaxItem));
                setFetching(true);
                const snapshot = yield (0, firestore_2.getDocs)(ref);
                setFetching(false);
                if (accessorRef.current !== accessor) {
                    return;
                }
                const additionalList = snapshot.docs.map((doc) => doc.data());
                setLastSnapshot(snapshot.docs.length === currentMaxItem
                    ? snapshot.docs[snapshot.docs.length - 1]
                    : false);
                if (additionalList.length) {
                    setList(Object.assign([...list, ...additionalList], {
                        oldListId: getObjectId(list),
                        additionalList,
                    }));
                }
            }
            catch (e) {
                setError(e);
                setFetching(false);
            }
        });
        const filteredList = (0, hooks_1.useMemoWithPrev)((oldFilteredList) => {
            const filteredList = (() => {
                if (oldFilteredList &&
                    oldFilteredList.listId === list.oldListId &&
                    oldFilteredList.originFilter === filter) {
                    const additionalFilteredList = oldFilteredList.filter(filter);
                    if (additionalFilteredList.length) {
                        return [...oldFilteredList, ...additionalFilteredList];
                    }
                    else {
                        return oldFilteredList;
                    }
                }
                return list.filter(filter);
            })();
            return Object.assign(filteredList, {
                listId: getObjectId(list),
                originFilter: filter,
            });
        }, [filter, list]);
        const shouldRefresh = filteredList.length - range.max < prefetchItems;
        (0, react_1.useEffect)(() => {
            if (!fetching && shouldRefresh) {
                refresh();
            }
        });
        (0, react_1.useEffect)(() => {
            setList([]);
            setRange({ max: 0 });
            setLastSnapshot(undefined);
        }, [accessor]);
        (0, react_1.useMemo)(() => {
            if (error)
                console.error(error);
        }, [error]);
        const get = (0, react_1.useMemo)(() => {
            return () => __awaiter(void 0, void 0, void 0, function* () {
                const list = yield (accessor === null || accessor === void 0 ? void 0 : accessor.get());
                return list === null || list === void 0 ? void 0 : list.filter(filter);
            });
        }, [accessor]);
        const refresher = (0, react_1.useMemo)(() => ({ currentTime: Date.now() }), [filter, accessor]);
        const reload = (0, react_1.useCallback)(() => {
            setList([]);
            setRange({ max: 0 });
            setLastSnapshot(undefined);
        }, []);
        return Object.assign(Object.assign({ list: filteredList, loading: false, fetching: lastSnapshot !== false && (fetching || shouldRefresh), hasMore: lastSnapshot !== false, error,
            setRange }, accessor), { get,
            refresher,
            reload,
            refresh });
    };
};
exports.createProgressiveQueryHook = createProgressiveQueryHook;
let currentObjectId = 0;
// eslint-disable-next-line @typescript-eslint/ban-types
const objectIdMap = new WeakMap();
// eslint-disable-next-line @typescript-eslint/ban-types
const getObjectId = (obj) => {
    if (!objectIdMap.has(obj)) {
        objectIdMap.set(obj, ++currentObjectId);
    }
    return objectIdMap.get(obj);
};
